import { REGION_TAG_LABELS, type RegionTagLabelEntry, type RegionTagType } from './region-tag-label-data';

/**
 * TEC-482: per-locale display labels + type classification for regionTags facet values. Without
 * this, the search filter shows raw gazetteer place ids (e.g. "658225") for city/admin1/country
 * entries instead of "Helsinki", only continent codes and recruiting-region codes were ever
 * human-readable on their own.
 *
 * Generated by scripts/generate-region-tag-labels.ts from the same checked-in seed data the
 * gazetteer itself is built from (scripts/data/geonames/*.json,
 * scripts/data/recruiting-regions.json), re-run that script by hand if the seed data changes.
 *
 * `type` lets the search UI group facet values (regions / continents / countries / Finland)
 * instead of rendering one flat ~1,200-item list, see FilterBuildingBlocks.tsx's
 * `processedFacetItems` regionTags branch.
 */
export type { RegionTagType, RegionTagLabelEntry } from './region-tag-label-data';

const LABELS = REGION_TAG_LABELS;

/** ISO 3166-1 alpha-2 -> country placeId, built lazily on first use from the country entries. */
let countryCodeToPlaceId: Map<string, string> | null = null;

/** Falls back to the English label, then the raw value, when unknown or the locale is missing, never throws, never shows nothing. */
export function getRegionTagLabel(value: string, locale: 'en' | 'fi' | 'sv' = 'en'): string {
  const entry = LABELS[value];
  if (!entry) return value;
  return entry[locale] || entry.en || value;
}

export function getRegionTagType(value: string): RegionTagType | undefined {
  return LABELS[value]?.type;
}

/** True when `value` has a real display label (i.e. is a known gazetteer place / region code), false for raw unknown ids. */
export function hasRegionTagLabel(value: string): boolean {
  return Boolean(LABELS[value]);
}

/**
 * Maps an ISO 3166-1 alpha-2 country code (the format `remoteEligibility.ref` uses for
 * `scopeType: 'country'`, and `remoteEligibleCountryCodes` stores) to that country's gazetteer
 * placeId, i.e. its `regionTags` value. Undefined for an unknown code, never throws.
 */
export function getRegionTagValueForCountryCode(countryCode: string): string | undefined {
  if (!countryCodeToPlaceId) {
    countryCodeToPlaceId = new Map<string, string>();
    for (const [placeId, entry] of Object.entries(LABELS)) {
      if (entry.type === 'country' && entry.countryCode) countryCodeToPlaceId.set(entry.countryCode, placeId);
    }
  }
  return countryCodeToPlaceId.get(countryCode.toUpperCase());
}

/**
 * The reverse of {@link getRegionTagValueForCountryCode}: a country placeId's own ISO 3166-1
 * alpha-2 code, e.g. for rendering a flag emoji on the map's country bubbles (see
 * countryCodeToFlagEmoji in components/map/map-marker-dom.ts). Undefined for a non-country value
 * or an unknown placeId, never throws.
 */
export function getCountryCodeForRegionTagValue(value: string): string | undefined {
  return LABELS[value]?.type === 'country' ? LABELS[value]?.countryCode : undefined;
}

/** name (any locale, lowercased) -> city placeId, built lazily on first use. */
let cityNameToPlaceId: Map<string, string> | null = null;
const FINLAND_COUNTRY_SUFFIX_WORDS = new Set(['finland', 'suomi']);

/**
 * TEC-482 Phase 8: reverse lookup from a Finnish city's display name to its gazetteer placeId —
 * the bridge from an incoming legacy display string (a decoded URL slug, an old `location` facet
 * filter value, a `location=` query param) to the placeId keys the migrated data-generation
 * pipelines now use. Accepts a bare name ("Helsinki") or one with the old flat-facet country
 * suffix ("Helsinki, Finland" / "Turku, Suomi"), case-insensitive, matched against the city's
 * en/fi/sv labels. Undefined for anything not a recognised Finnish city name.
 *
 * Safe to key on name alone without a country check because the checked-in gazetteer seed is
 * Finland-only as of this writing (scripts/data/geonames/README.md) — every `type: 'city'` entry
 * in region-tag-labels.json is a Finnish city, so there's no other-country collision risk yet.
 * Revisit (scope this to Finland placeIds explicitly) if the gazetteer is ever seeded
 * internationally.
 */
/** Lowercased label (en/fi/sv) -> placeId across EVERY entry type, built lazily. City entries win
 * over same-named admin1/country entries only where a name collides, which the Finland-only seed
 * makes rare (see getFinlandCityPlaceIdForName). */
let anyLabelToPlaceId: Map<string, string> | null = null;
const TYPE_PRIORITY: Record<RegionTagType, number> = { city: 0, admin1: 1, country: 2, recruitingRegion: 3, continent: 4 };

function ensureAnyLabelMap(): Map<string, string> {
  if (anyLabelToPlaceId) return anyLabelToPlaceId;
  const map = new Map<string, string>();
  const entries = Object.entries(LABELS).sort((a, b) => TYPE_PRIORITY[a[1].type] - TYPE_PRIORITY[b[1].type]);
  for (const [placeId, entry] of entries) {
    for (const label of [entry.en, entry.fi, entry.sv]) {
      const key = label?.trim().toLowerCase();
      if (key && !map.has(key)) map.set(key, placeId);
    }
  }
  anyLabelToPlaceId = map;
  return map;
}

/**
 * Resolves a free-text place name ("Helsinki", "Helsinki, Finland", "Uusimaa", "Finland",
 * "Suomi", "EMEA") to its regionTags value. Tries the whole string, then the part before the last
 * comma (legacy `location` facet values carry a country suffix). Undefined when unknown; never
 * throws. Used to migrate profiles saved under the old flat `location` strings (TEC-482).
 */
export function findRegionTagByName(name: string): string | undefined {
  const trimmed = name.trim();
  if (!trimmed) return undefined;
  const map = ensureAnyLabelMap();
  const direct = map.get(trimmed.toLowerCase());
  if (direct) return direct;
  const city = getFinlandCityPlaceIdForName(trimmed);
  if (city) return city;
  const commaIdx = trimmed.lastIndexOf(',');
  if (commaIdx > 0) {
    const head = trimmed.slice(0, commaIdx).trim().toLowerCase();
    const viaHead = map.get(head);
    if (viaHead) return viaHead;
  }
  return undefined;
}

/** Lowercased country label (en/fi/sv, with and without a leading "the ") -> country placeId, built lazily. */
let countryNameToPlaceId: Map<string, string> | null = null;

/**
 * Country name in any locale ("Belgium", "Tanska", "Netherlands", "The Netherlands") to that
 * country's regionTags value. Used to attach a foreign city the client label table has no entry
 * for ("Saint-Ghislain, Belgium") to its country. Undefined when unknown; never throws.
 */
export function findCountryRegionTagByName(name: string): string | undefined {
  if (!countryNameToPlaceId) {
    countryNameToPlaceId = new Map<string, string>();
    for (const [placeId, entry] of Object.entries(LABELS)) {
      if (entry.type !== 'country') continue;
      for (const label of [entry.en, entry.fi, entry.sv]) {
        const key = label?.trim().toLowerCase();
        if (!key) continue;
        countryNameToPlaceId.set(key, placeId);
        if (key.startsWith('the ')) countryNameToPlaceId.set(key.slice(4), placeId);
      }
    }
  }
  const key = name.trim().toLowerCase();
  return countryNameToPlaceId.get(key) ?? countryNameToPlaceId.get(key.replace(/^the /, ''));
}

export interface RegionTagSearchHit {
  value: string;
  label: string;
  type: RegionTagType;
}

/** Type-ahead over every labelled place: prefix matches first, then substring, cities before regions before countries. */
export function searchRegionTags(query: string, locale: 'en' | 'fi' | 'sv', limit = 8): RegionTagSearchHit[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const prefix: RegionTagSearchHit[] = [];
  const inner: RegionTagSearchHit[] = [];
  for (const [value, entry] of Object.entries(LABELS)) {
    const label = entry[locale] || entry.en;
    const candidates = [entry.en, entry.fi, entry.sv].filter(Boolean).map((l) => l.toLowerCase());
    if (candidates.some((l) => l.startsWith(q))) prefix.push({ value, label, type: entry.type });
    else if (candidates.some((l) => l.includes(q))) inner.push({ value, label, type: entry.type });
  }
  const byType = (a: RegionTagSearchHit, b: RegionTagSearchHit) => TYPE_PRIORITY[a.type] - TYPE_PRIORITY[b.type] || a.label.localeCompare(b.label);
  return [...prefix.sort(byType), ...inner.sort(byType)].slice(0, limit);
}

export function getFinlandCityPlaceIdForName(name: string): string | undefined {
  if (!cityNameToPlaceId) {
    cityNameToPlaceId = new Map<string, string>();
    for (const [placeId, entry] of Object.entries(LABELS)) {
      if (entry.type !== 'city') continue;
      for (const label of [entry.en, entry.fi, entry.sv]) {
        if (label) cityNameToPlaceId.set(label.toLowerCase(), placeId);
      }
    }
  }
  const trimmed = name.trim();
  if (!trimmed) return undefined;
  const commaIdx = trimmed.lastIndexOf(',');
  if (commaIdx <= 0) return cityNameToPlaceId.get(trimmed.toLowerCase());
  const town = trimmed.slice(0, commaIdx).trim();
  const suffix = trimmed.slice(commaIdx + 1).trim().toLowerCase();
  if (!town || !FINLAND_COUNTRY_SUFFIX_WORDS.has(suffix)) return undefined;
  return cityNameToPlaceId.get(town.toLowerCase());
}

/** Slug-safe key: ASCII-folded, lowercase, letters and digits only ("S:t Michel" and the URL's "st_michel" both give "stmichel"). */
function townNameKey(name: string): string {
  return name.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** townNameKey -> the town's labels, or null when two different towns share the key. */
let townKeyToLabels: Map<string, RegionTagLabelEntry | null> | null = null;

/**
 * Every locale's name for a Finnish town, from any of its names in any form: display ("Jakobstad"),
 * lowercase, or ASCII-folded URL slug text ("hameenlinna", "st michel"). Undefined for an unknown
 * or ambiguous name. Lets a location URL in one language's spelling be rewritten to the town's
 * name in another (pietarsaari_finland is jakobstad_finland on the Swedish site).
 */
export function getFinlandTownLabels(name: string): RegionTagLabelEntry | undefined {
  if (!townKeyToLabels) {
    townKeyToLabels = new Map();
    for (const entry of Object.values(LABELS)) {
      if (entry.type !== 'city') continue;
      for (const label of new Set([entry.en, entry.fi, entry.sv])) {
        const key = townNameKey(label);
        if (!key) continue;
        const prev = townKeyToLabels.get(key);
        if (prev === undefined) townKeyToLabels.set(key, entry);
        else if (prev && (prev.en !== entry.en || prev.fi !== entry.fi || prev.sv !== entry.sv)) townKeyToLabels.set(key, null);
      }
    }
  }
  return townKeyToLabels.get(townNameKey(name)) ?? undefined;
}
